import type { MonoTypeOperatorFunction } from 'rxjs';
import { defer, throwError, timer } from 'rxjs';
import { concatMap, map, retryWhen } from 'rxjs/operators';

import type { AgentSessionData } from '@sb/feathers-types';
import { mapPaginated } from '@sb/feathers-types';
import type { Unsubscribe } from '@sb/remote-control/types';

import { getAgentSessionsService } from './getAgentSessionsService';

/** Caps the chat-history list; the newest sessions win via `$sort`. */
const SESSION_LIMIT = 100;

/** Resubscribe attempts before the error is handed to the caller. */
const MAX_RETRIES = 3;

const RETRY_BASE_DELAY_MS = 1000;

/**
 * Resubscribes on error with exponential backoff, then gives up and
 * rethrows so the caller can surface a retry affordance.
 *
 * An rxjs error notification *terminates* the observable, which for a
 * live list means it stops updating for the rest of the page session.
 * The app-wide pipe in `getApp()` only recovers two specific cases
 * (`retryOnNotAuthenticated`, `retryOnTimeout`); everything else —
 * a `GeneralError` when feathers-bot is up but its database isn't
 * reachable yet, say — is rethrown and kills the stream.
 *
 * This is deliberately applied per-subscription rather than added to
 * that shared pipe: blanket-retrying every watch stream in the app is
 * a much larger behavioural change than this list warrants.
 */
function retryWithBackoff<T>(): MonoTypeOperatorFunction<T> {
  return retryWhen<T>((errors$) =>
    errors$.pipe(
      concatMap((error, attempt) => {
        if (attempt >= MAX_RETRIES) return throwError(error);

        return timer(RETRY_BASE_DELAY_MS * 2 ** attempt);
      }),
    ),
  );
}

export interface OnGetAgentSessionsOptions {
  /**
   * Restricts the list to sessions bound to this routine. `null` lists
   * every session across all routines (the global chat-history view).
   */
  routineID: string | null;
  onSnapshot: (sessions: AgentSessionData[]) => void;
  onError: (error: Error) => void;
}

/**
 * Live chat-session list. Emits immediately with the current rows, then
 * re-emits whenever a session is created, renamed, or soft-deleted —
 * so a chat started in this tab, another tab, or by the agent itself
 * appears in the history without a page reload.
 *
 * `feathers-reactive`'s `smart` list strategy does the merging: it folds
 * each matching `created` / `patched` / `removed` event into the cached
 * page and re-applies the query's `$sort` and `$limit`, so callers get a
 * correctly ordered list rather than raw events to reconcile themselves.
 *
 * `deleted: false` has to be part of the query, not a post-filter: the
 * strategy decides whether an event still belongs in the list by matching
 * it against this query, and a soft-delete arrives as a `patched` event
 * flipping `deleted` to true. Without the constraint that patch would
 * keep the row in the list instead of evicting it.
 */
export function onGetAgentSessions({
  routineID,
  onSnapshot,
  onError,
}: OnGetAgentSessionsOptions): Unsubscribe {
  // `defer` so each retry attempt builds the query afresh. Resubscribing
  // to an already-constructed `watch().find()` happens to re-run the
  // query too, but only through three layers of feathers-reactive
  // internals lining up — its cache entry is dropped by a `finalize` on
  // teardown, its share-replay subject resets at refCount 0, and the
  // innermost source is itself a `defer`. Retry semantics shouldn't rest
  // on that.
  const subscription = defer(() =>
    getAgentSessionsService()
      .watch()
      .find({
        query: {
          deleted: false,
          ...(routineID === null ? {} : { routineID }),
          $sort: { updatedAt: -1 },
          $limit: SESSION_LIMIT,
        },
      }),
  )
    .pipe(
      map((response) => mapPaginated(response, (row) => row)),
      // After `map` so a resubscribe replays the whole chain, and the
      // caller only sees `onError` once recovery has actually failed.
      retryWithBackoff(),
    )
    .subscribe({
      next: onSnapshot,
      error: (error: unknown) =>
        onError(error instanceof Error ? error : new Error(String(error))),
    });

  return () => subscription.unsubscribe();
}
